<template>
  <div class="credential-preview">
    <!-- Success Icon -->
    <div class="success-header">
      <div class="success-icon">
        <i class="ni ni-check-bold"></i>
      </div>
      <h3 class="success-title">Данные получены!</h3>
      <p class="success-subtitle">Проверьте захваченные данные перед сохранением</p>
    </div>

    <!-- Credentials List -->
    <div class="credentials-list">
      <!-- Phone (masked) -->
      <div class="credential-item">
        <div class="credential-icon">
          <i class="ni ni-mobile-button"></i>
        </div>
        <div class="credential-info">
          <label>Телефон</label>
          <div class="credential-value">{{ capturedData.phone || '-' }}</div>
        </div>
        <div class="status-badge captured">
          <i class="ni ni-check-bold"></i>
        </div>
      </div>

      <!-- Password Status -->
      <div class="credential-item">
        <div class="credential-icon">
          <i class="ni ni-lock-circle-open"></i>
        </div>
        <div class="credential-info">
          <label>Пароль</label>
          <div class="credential-value">
            <span v-if="capturedData.has_password" class="status-text success">
              <i class="ni ni-check-bold"></i> Захвачен
            </span>
            <span v-else class="status-text missing">
              <i class="ni ni-fat-remove"></i> Не захвачен
            </span>
          </div>
        </div>
        <div class="status-badge" :class="capturedData.has_password ? 'captured' : 'missing'">
          <i :class="capturedData.has_password ? 'ni ni-check-bold' : 'ni ni-fat-remove'"></i>
        </div>
      </div>

      <!-- Token Status -->
      <div class="credential-item">
        <div class="credential-icon">
          <i class="ni ni-key-25"></i>
        </div>
        <div class="credential-info">
          <label>Токен</label>
          <div class="credential-value">
            <span v-if="capturedData.has_token" class="status-text success">
              <i class="ni ni-check-bold"></i> Захвачен
            </span>
            <span v-else class="status-text missing">
              <i class="ni ni-fat-remove"></i> Не захвачен
            </span>
          </div>
        </div>
        <div class="status-badge" :class="capturedData.has_token ? 'captured' : 'missing'">
          <i :class="capturedData.has_token ? 'ni ni-check-bold' : 'ni ni-fat-remove'"></i>
        </div>
      </div>

      <!-- Device Name -->
      <div v-if="capturedData.device_name" class="credential-item">
        <div class="credential-icon">
          <i class="ni ni-tablet-button"></i>
        </div>
        <div class="credential-info">
          <label>Устройство</label>
          <div class="credential-value">{{ capturedData.device_name }}</div>
        </div>
        <div class="status-badge captured">
          <i class="ni ni-check-bold"></i>
        </div>
      </div>
    </div>

    <!-- Summary Notice -->
    <div class="summary-notice" :class="summaryClass">
      <i :class="summaryIcon"></i>
      <span>{{ summaryText }}</span>
    </div>

    <!-- Warning Notice -->
    <div class="warning-notice">
      <i class="ni ni-notification-70"></i>
      <span>
        После подтверждения данные будут сохранены на сервере и использованы для авторизации провайдера.
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CredentialPreview',
  props: {
    capturedData: {
      type: Object,
      required: true,
      default: function() {
        return {
          phone: '',
          has_password: false,
          has_token: false,
          device_name: ''
        };
      }
    }
  },
  computed: {
    allFieldsCaptured() {
      return this.capturedData.has_password && this.capturedData.has_token;
    },
    summaryClass() {
      return this.allFieldsCaptured ? 'success' : 'warning';
    },
    summaryIcon() {
      return this.allFieldsCaptured ? 'ni ni-check-bold' : 'ni ni-notification-70';
    },
    summaryText() {
      if (this.allFieldsCaptured) {
        return 'Все необходимые данные успешно захвачены.';
      } else if (this.capturedData.has_password && !this.capturedData.has_token) {
        return 'Токен не был захвачен. Авторизация может потребовать дополнительных шагов.';
      } else if (!this.capturedData.has_password && this.capturedData.has_token) {
        return 'Пароль не был захвачен. Некоторые функции могут быть недоступны.';
      } else {
        return 'Пароль и токен не захвачены. Рекомендуется повторить процедуру.';
      }
    }
  }
};
</script>

<style scoped lang="scss">
.credential-preview {
  position: relative;

  .success-header {
    text-align: center;
    margin-bottom: 2rem;

    .success-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background-color: rgba(82, 196, 26, 0.1);
      color: #52c41a;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1rem;

      i {
        font-size: 1.75rem;
      }
    }

    .success-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.5rem;
    }

    .success-subtitle {
      color: #606266;
      font-size: 0.9rem;
      margin: 0;
    }
  }

  .credentials-list {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 0.5rem;
    margin-bottom: 1.5rem;

    .credential-item {
      display: flex;
      align-items: center;
      padding: 1rem;
      background-color: #fff;
      border-radius: 6px;
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }

      .credential-icon {
        width: 40px;
        height: 40px;
        border-radius: 8px;
        background-color: rgba(67, 97, 238, 0.1);
        color: #4361EE;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        flex-shrink: 0;

        i {
          font-size: 1.1rem;
        }
      }

      .credential-info {
        flex: 1;
        min-width: 0;

        label {
          display: block;
          font-size: 0.75rem;
          color: #909399;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
          font-weight: 600;
        }

        .credential-value {
          font-size: 1rem;
          color: #333;
          font-weight: 500;
          word-break: break-all;

          .status-text {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            font-size: 0.9rem;

            i {
              font-size: 0.75rem;
            }

            &.success {
              color: #52c41a;
            }

            &.missing {
              color: #909399;
            }
          }
        }
      }

      .status-badge {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-left: 1rem;

        i {
          font-size: 0.75rem;
        }

        &.captured {
          background-color: rgba(82, 196, 26, 0.1);
          color: #52c41a;
        }

        &.missing {
          background-color: rgba(144, 147, 153, 0.1);
          color: #909399;
        }
      }
    }
  }

  .summary-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 1rem;

    i {
      flex-shrink: 0;
      margin-top: 0.1rem;
      font-size: 1rem;
    }

    &.success {
      background-color: rgba(82, 196, 26, 0.1);
      color: #389e0d;
    }

    &.warning {
      background-color: rgba(255, 193, 7, 0.1);
      color: #856404;
    }
  }

  .warning-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    background-color: rgba(67, 97, 238, 0.05);
    border-radius: 6px;
    color: #606266;
    font-size: 0.875rem;
    line-height: 1.5;

    i {
      flex-shrink: 0;
      margin-top: 0.1rem;
      font-size: 1rem;
      color: #4361EE;
    }
  }
}
</style>
